import { UseQueryResult, UseMutationResult, type UseMutationOptions } from '@tanstack/react-query'
import {
  DomesticCartConverterResponse,
  DomesticCartPurchaseResponse,
} from '~/api/routes/domestic/cart/controller/type'
import { useReactQuery, useReactMutation } from '~/app/react-query'
import AxiosClient, { IAxiosClientError } from '~/app/react-query/axios'
import {
  PostCartCheckStockDto,
  PostCartUpdateDto,
  PostCartDeleteDto,
  PostCartOrderDto,
  PostCartDayuseTimeLinesDto,
} from '~/api/routes/domestic/cart/controller/dto'
import { DomesticCartDayuseTimeLinesConverterResponse } from '~/api/routes/domestic/cart/controller/type'
import {
  DOMESTIC_CART_QUERY_KEY,
  DOMESTIC_CART_DAYUSE_TIME_LINES_QUERY_KEY,
} from '~/app/react-query/domestic/cart/constants'

// ============================================================================
// Query: 장바구니 조회
// ============================================================================

/**
 * 국내 장바구니 데이터를 가져오는 API 호출 함수
 */
const getDomesticCart = () => {
  const axios = new AxiosClient()
  return axios.instance.get('/api/domestic/cart')
}
/**
 * 국내 장바구니 조회를 위한 React Query Hook
 */
export const useDomesticCartQuery = (): UseQueryResult<
  DomesticCartConverterResponse,
  IAxiosClientError
> => {
  return useReactQuery({
    queryKey: [DOMESTIC_CART_QUERY_KEY],
    queryFn: getDomesticCart,
    queryOptions: {
      refetchOnWindowFocus: false,
    },
    target: 'domestic',
  })
}
// ============================================================================
// Query: 장바구니 대실 시간 정보 조회
// ============================================================================
/**
 * 장바구니 대실 시간 정보를 가져오는 API 호출 함수
 */
const getDomesticCartDayuseTimeLines = (
  body: PostCartDayuseTimeLinesDto['body'] | undefined
): Promise<DomesticCartDayuseTimeLinesConverterResponse> => {
  const axios = new AxiosClient()
  return axios.instance.post('/api/domestic/cart/dayuse-time-lines', body)
}
/**
 * 장바구니 대실 시간 정보를 가져오는 React Query Hook
 */
export const useDomesticCartDayuseTimeLinesQuery = (
  optionId: string,
  body: PostCartDayuseTimeLinesDto['body'] | undefined,
  enabled: boolean = false
): UseQueryResult<DomesticCartDayuseTimeLinesConverterResponse, IAxiosClientError> => {
  return useReactQuery({
    queryKey: [DOMESTIC_CART_DAYUSE_TIME_LINES_QUERY_KEY, optionId],
    queryFn: () => getDomesticCartDayuseTimeLines(body),
    queryOptions: {
      refetchOnWindowFocus: false,
      enabled: enabled && body !== undefined,
    },
    target: 'domestic',
  })
}
// ============================================================================
// Mutation: 장바구니 옵션 검증
// ============================================================================
/**
 * 장바구니 옵션의 유효성을 검증하는 API 호출 함수
 */
const checkCartOptionsValidation = (
  body: PostCartCheckStockDto['body']
): Promise<{ success: boolean }> => {
  const axios = new AxiosClient()
  return axios.instance.post(`/api/domestic/cart/check-validation`, body)
}
/**
 * 장바구니 옵션 검증을 위한 React Query Mutation Hook
 */
export const useDomesticCartCheckValidationMutation = (): UseMutationResult<
  { success: boolean },
  IAxiosClientError,
  PostCartCheckStockDto['body']
> => {
  return useReactMutation({
    mutationFn: checkCartOptionsValidation,
    target: 'domestic',
  })
}
// ============================================================================
// Mutation: 장바구니 삭제
// ============================================================================
/**
 * 장바구니 항목을 삭제하는 API 호출 함수
 */
const deleteDomesticCart = (body: PostCartDeleteDto['body']): Promise<{ success: boolean }> => {
  const axios = new AxiosClient()
  return axios.instance.delete('/api/domestic/cart', { data: body })
}
/**
 * 장바구니 삭제를 위한 React Query Mutation Hook
 */
export const useDomesticCartDeleteMutation = (
  mutationOptions?: Omit<
    UseMutationOptions<unknown, unknown, PostCartDeleteDto['body'], DomesticCartConverterResponse>,
    'mutationKey'
  > & {
    useCommonErrorHandler?: boolean
  }
): UseMutationResult<unknown, unknown, PostCartDeleteDto['body']> => {
  return useReactMutation({
    mutationFn: deleteDomesticCart,
    target: 'domestic',
    options: {
      ...mutationOptions,
      retry: 0,
      useCommonErrorHandler: false,
    },
  })
}
/**
 * 장바구니 옵션을 업데이트하는 API 호출 함수
 */
const updateDomesticCartOptions = (
  body: PostCartUpdateDto['body']
): Promise<{ success: boolean }> => {
  const axios = new AxiosClient()
  return axios.instance.post('/api/domestic/cart/update', body)
}
/**
 * 장바구니 옵션을 업데이트하는 React Query Mutation Hook
 */
export const useDomesticCartUpdateMutation = (
  options?: UseMutationOptions<
    unknown,
    IAxiosClientError,
    PostCartUpdateDto['body'],
    DomesticCartConverterResponse
  >
): UseMutationResult<unknown, IAxiosClientError, PostCartUpdateDto['body']> => {
  return useReactMutation({
    mutationFn: updateDomesticCartOptions,
    target: 'domestic',
    options: { retry: 0, useCommonErrorHandler: false, ...options },
  })
}

const purchaseDomesticCart = (
  body: PostCartOrderDto['body']
): Promise<DomesticCartPurchaseResponse> => {
  const axios = new AxiosClient()
  return axios.instance.post('/api/domestic/cart/order', body)
}

export const useDomesticCartPurchaseMutation = (): UseMutationResult<
  DomesticCartPurchaseResponse,
  IAxiosClientError,
  PostCartOrderDto['body']
> => {
  return useReactMutation({
    mutationFn: purchaseDomesticCart,
    target: 'domestic',
    options: {
      retry: 0,
    },
  })
}
